import styled from "@emotion/styled";
import { FieldErrors, UseFormRegister, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  SubmitButton,
  TextField,
  Typography,
} from "@avenue-8/sidekick-ui";
import { useUIStore } from "@store/ui.store";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ResponsiveSheetDialog } from "@shared/components/ResponsiveSheetDialog/ResponsiveSheetDialog";
import { toast } from "@shared/services/toast";
import { getFetchClientErrorMessage } from "@shared/apis/fetch-clients/fetch-client-error-message";
import { listContactsQueryKey, useCreateContact } from "@shared/generated/crm";
import {
  contactActivityQueryKey,
  countContactsQueryKey,
} from "@shared/generated/radar";
import {
  CONTACT_FIELD_DEFINITIONS,
  ContactFieldDefinition,
  ContactFieldId,
} from "@features/Contacts/components/ContactBriefingHeader/contact-fields-helpers";
import {
  LeadAccordion,
  LeadAccordionDetails,
  LeadAccordionSummary,
} from "@features/Contacts/components/LeadAccordion";
import {
  NEW_CONTACT_DEFAULT_VALUES,
  newContactFormSchema,
  toCreateContactBody,
} from "./new-contact-form-helpers";
import {
  isSocialHandleFieldId,
  parseFacebookHandleInput,
  parseInstagramHandleInput,
  parseLinkedInHandleInput,
  validateSocialHandleFields,
  type SocialHandleFieldId,
} from "@features/Contacts/components/ContactBriefingHeader/contact-summary-chips-helpers";

type NewContactDialogProps = {
  open: boolean;
  onClose: () => void;
};

type NewContactFormValues = Record<string, string>;

type ContactFormFieldLayout = { id: ContactFieldId; fullWidth?: boolean };

const VISIBLE_FIELDS: ContactFormFieldLayout[] = [
  { id: "firstName" },
  { id: "lastName" },
  { id: "preferredName" },
  { id: "fullName" },
  { id: "email", fullWidth: true },
  { id: "phone", fullWidth: true },
  { id: "instagramHandle" },
  { id: "linkedinHandle" },
  { id: "facebookHandle" },
  { id: "links", fullWidth: true },
];
const OCCUPATION_FIELDS: ContactFormFieldLayout[] = [
  { id: "company" },
  { id: "jobTitle" },
];
const ADDRESS_FIELDS: ContactFormFieldLayout[] = [
  { id: "address", fullWidth: true },
  { id: "city", fullWidth: true },
  { id: "state" },
  { id: "postalCode" },
];

const FIELD_PLACEHOLDERS: Record<string, string> = {
  email: "email@example.com, other@example.com",
  phone: "+1 555 0100, +1 555 0101",
  links: "example.com, example2.com",
  instagramHandle: "janedoe",
  linkedinHandle: "janedoe",
  facebookHandle: "janedoe",
};

const fieldDefinitionsById: Record<string, ContactFieldDefinition> =
  Object.fromEntries(
    CONTACT_FIELD_DEFINITIONS.map((field) => [field.id, field]),
  );

export const NewContactDialog = (props: NewContactDialogProps) => {
  const { open, onClose } = props;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirmDialog = useUIStore((s) => s.confirmDialog);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    reset,
    formState: { errors, isDirty },
  } = useForm<NewContactFormValues>({
    defaultValues: NEW_CONTACT_DEFAULT_VALUES,
  });

  const {
    mutate: createContact,
    isPending,
    reset: resetCreateContactMutation,
  } = useCreateContact({
    mutation: {
      onSuccess: (contact) => {
        queryClient.invalidateQueries({ queryKey: listContactsQueryKey() });
        queryClient.invalidateQueries({ queryKey: countContactsQueryKey() });
        queryClient.invalidateQueries({
          queryKey: contactActivityQueryKey(),
        });
        reset(NEW_CONTACT_DEFAULT_VALUES);
        onClose();
        navigate(`/contacts/${contact.id}`);
      },
      onError: (error) => {
        toast.error(
          getFetchClientErrorMessage(
            error,
            "Something went wrong creating this contact. Please try again.",
          ),
        );
      },
    },
  });

  const handleClose = async () => {
    if (isPending) return;
    if (isDirty) {
      const confirmed = await confirmDialog({
        title: "Discard new contact?",
        content: (
          <Typography>
            You&apos;ll lose the information you&apos;ve entered.
          </Typography>
        ),
        confirmButtonText: "Discard",
      });
      if (!confirmed) return;
    }
    reset(NEW_CONTACT_DEFAULT_VALUES);
    resetCreateContactMutation();
    onClose();
  };

  const onSubmit = (values: NewContactFormValues) => {
    clearErrors();
    const socialHandleErrors = validateSocialHandleFields(values);
    const socialHandleErrorEntries = Object.entries(socialHandleErrors);
    if (socialHandleErrorEntries.length > 0) {
      socialHandleErrorEntries.forEach(([fieldId, message]) => {
        setError(fieldId, { type: "social-handle", message });
      });
      return;
    }

    const parsed = newContactFormSchema.safeParse(toCreateContactBody(values));
    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        const [firstPathPart] = issue.path;
        if (typeof firstPathPart !== "string") return;
        setError(firstPathPart, { type: "zod", message: issue.message });
      });
      return;
    }
    createContact({ data: parsed.data });
  };

  const normalizeSocialHandleOnBlur = (
    fieldId: SocialHandleFieldId,
    rawValue: string,
  ) => {
    const result =
      fieldId === "instagramHandle"
        ? parseInstagramHandleInput(rawValue)
        : fieldId === "linkedinHandle"
          ? parseLinkedInHandleInput(rawValue)
          : parseFacebookHandleInput(rawValue);
    if (result.ok) {
      setValue(fieldId, result.handle, { shouldDirty: true });
    }
  };

  return (
    <ResponsiveSheetDialog
      open={open}
      onClose={handleClose}
      title="New contact"
      maxWidth="md"
    >
      <FormLayout onSubmit={handleSubmit(onSubmit)}>
        <FormScroll>
          <ContactFieldsGrid
            fields={VISIBLE_FIELDS}
            register={register}
            errors={errors}
            disabled={isPending}
            onSocialHandleBlur={normalizeSocialHandleOnBlur}
          />
          <AccordionsStack>
            <LeadAccordion disableGutters defaultExpanded={false}>
              <LeadAccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
              >
                <Typography variant="subtitle1">Occupation</Typography>
              </LeadAccordionSummary>
              <LeadAccordionDetails>
                <ContactFieldsGrid
                  fields={OCCUPATION_FIELDS}
                  register={register}
                  errors={errors}
                  disabled={isPending}
                />
              </LeadAccordionDetails>
            </LeadAccordion>
            <LeadAccordion disableGutters defaultExpanded={false}>
              <LeadAccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
              >
                <Typography variant="subtitle1">Address</Typography>
              </LeadAccordionSummary>
              <LeadAccordionDetails>
                <ContactFieldsGrid
                  fields={ADDRESS_FIELDS}
                  register={register}
                  errors={errors}
                  disabled={isPending}
                />
              </LeadAccordionDetails>
            </LeadAccordion>
          </AccordionsStack>
        </FormScroll>
        <FormActions>
          <Button variant="text" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <SubmitButton
            variant="contained"
            isLoading={isPending}
            loadingText="Creating..."
          >
            Create
          </SubmitButton>
        </FormActions>
      </FormLayout>
    </ResponsiveSheetDialog>
  );
};

type ContactFieldsGridProps = {
  fields: ContactFormFieldLayout[];
  register: UseFormRegister<NewContactFormValues>;
  errors: FieldErrors<NewContactFormValues>;
  disabled: boolean;
  onSocialHandleBlur?: (fieldId: SocialHandleFieldId, rawValue: string) => void;
};

const ContactFieldsGrid = (props: ContactFieldsGridProps) => {
  const { fields, register, errors, disabled, onSocialHandleBlur } = props;
  return (
    <FieldsGrid>
      {fields.map(({ id, fullWidth }) => {
        const field = fieldDefinitionsById[id];
        return (
          <FieldWrapper key={id} $fullWidth={fullWidth}>
            <TextField
              label={field.label}
              placeholder={FIELD_PLACEHOLDERS[id]}
              disabled={disabled}
              {...register(id, {
                onBlur: isSocialHandleFieldId(id)
                  ? (event) => onSocialHandleBlur?.(id, event.target.value)
                  : undefined,
              })}
              fieldError={errors[id]}
            />
          </FieldWrapper>
        );
      })}
    </FieldsGrid>
  );
};

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;

  ${(p) => p.theme.media.mobileDown} {
    grid-template-columns: 1fr;
  }
`;

const FieldWrapper = styled.div<{ $fullWidth?: boolean }>`
  ${(p) => p.$fullWidth && "grid-column: 1 / -1;"}
`;

const AccordionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.75rem;
`;

const FormLayout = styled.form`
  display: flex;
  flex-direction: column;

  ${(p) => p.theme.media.laptopDown} {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
`;

const FormScroll = styled.div`
  ${(p) => p.theme.media.laptopDown} {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-bottom: 1rem;
  }
`;

const FormActions = styled.div`
  margin-top: 1.5rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;

  ${(p) => p.theme.media.laptopDown} {
    flex-shrink: 0;
    margin-top: 0;
    padding-top: 1rem;
    background-color: ${(p) => p.theme.colors.background};
    border-top: 1px solid ${(p) => p.theme.colors.border2};
  }
`;
